import { ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatDateTime, parseInstant } from '@/lib/dataFormat';
import { intervalDuration, relativeTimeLabel, zoneOffset } from '@/lib/eventTime';
export function RelativeTime({
  at,
  now,
  timeZone = 'Europe/Moscow',
  zoneLabel,
}: {
  at: string | Date;
  now?: number;
  timeZone?: string;
  zoneLabel?: string;
}) {
  const [clock, setClock] = useState(Date.now);
  useEffect(() => {
    if (now !== undefined) return;
    const timer = window.setInterval(() => setClock(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, [now]);
  const date = parseInstant(at);
  return (
    <time
      data-slot="relative-time"
      dateTime={date?.toISOString()}
      title={
        date ? formatDateTime(at, timeZone) + ' · ' + (zoneLabel ?? zoneOffset(date, timeZone)) : undefined
      }
      className="text-xs text-fg-muted"
    >
      {relativeTimeLabel(at, now ?? clock)}
    </time>
  );
}
export function TimeInterval({
  start,
  end,
  timeZone = 'Europe/Moscow',
  zoneLabel,
}: {
  start: string | Date;
  end: string | Date;
  timeZone?: string;
  zoneLabel?: string;
}) {
  const from = parseInstant(start),
    to = parseInstant(end),
    duration = intervalDuration(start, end);
  if (!from || !to || !duration) return <span className="text-xs text-fg-muted">Интервал неизвестен</span>;
  const date = new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const clock = new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const fromOffset = zoneOffset(from, timeZone),
    toOffset = zoneOffset(to, timeZone);
  return (
    <div data-slot="time-interval" className="flex min-w-0 flex-col gap-2">
      <div className="flex min-w-0 items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-2xs text-fg-muted">Начало</p>
          <time
            dateTime={from.toISOString()}
            title={formatDateTime(start, timeZone) + ' · ' + fromOffset}
            className="mt-1 block"
          >
            <span className="block text-sm font-medium tabular-nums">{clock.format(from)}</span>
            <span className="block text-xs text-fg-muted">{date.format(from)}</span>
          </time>
        </div>
        <ArrowRight aria-hidden className="size-4 shrink-0 text-fg-muted" />
        <div className="min-w-0 flex-1">
          <p className="text-2xs text-fg-muted">Конец</p>
          <time
            dateTime={to.toISOString()}
            title={formatDateTime(end, timeZone) + ' · ' + toOffset}
            className="mt-1 block"
          >
            <span className="block text-sm font-medium tabular-nums">{clock.format(to)}</span>
            <span className="block text-xs text-fg-muted">{date.format(to)}</span>
          </time>
        </div>
      </div>
      <p className="text-xs text-fg-muted">
        <span className="font-medium text-fg">{duration}</span> · {zoneLabel && zoneLabel + ' · '}
        {fromOffset}
        {fromOffset !== toOffset && ' → ' + toOffset}
      </p>
    </div>
  );
}
